import { makeStyles } from '@mui/styles'
import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import { ContractType, LegalEntityParty, Terms } from '@types'
import {
  commonNoActionButtonStyle,
  commonProceedButtonStyle
} from 'components/request-action/styles/styles'
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'
import { CreateGxInitialValues } from 'contexts/infer-types/create-initial'
import { Dayjs } from 'dayjs'
import { useFormik } from 'formik'
import { useSocketConnection } from 'hooks/useSocketConnection'
import Item from 'pages/dashboard/sub-sections/items'
import { base64ToPdf, sendPDFCall } from 'pages/dashboard/utils'
import { useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { startLoadingPDF } from 'redux/slices/pdf'
import { PATH_DASHBOARD } from 'routes/paths'
import * as Yup from 'yup'
import { getMinExpiryDays, getExpiryDateErrorMessage } from 'utils/expiryDateConfig'
import { RootState, dispatch, useSelector } from '../../../../redux/store'
import { PRIMARY } from '../../../../theme/palette'
import { AmountAndCurrency } from '../AmountAndCurrency'
import { TermsAndConditionsSelection } from '../autocomplete/DropDowns'
import { ExpiryDate, TimeRow } from '../section'
import { ElementBreak, Heading, SectionBreak } from './shared'
import { convertAmountStringToObject, defineAmountState } from './utils'
import { sanitizeInput } from 'components/request-action/sanitzeUnicode'
import { EntityDetailsRow } from '../EntityRow'
import { PointOfContact } from './types/PointOfContact'
import { ContractDetailsFields, InternalReferenceField } from './components'

const illegalCharacterMessage = 'Unsupported character replaced with acceptable equivalent'

export type InitialValues = CreateGxInitialValues & {
  name: string
  startDate: string
  endDate: string
}

const PaymentPerformanceGuarantee = () => {
  const navigate = useNavigate()

  // ===== Entity States =====
  const [, setApplicant] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [{ legalEntityCodeType: 'ABN', legalEntityCodeData: '' }]
  })
  const [, setBene] = useState<LegalEntityParty>({
    commonName: '',
    onboarded: true,
    legalEntityIdentifier: '',
    legalEntityCodes: [{ legalEntityCodeType: 'ABN', legalEntityCodeData: '' }]
  })
  const [, setIssuer] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [{ legalEntityCodeType: 'ABN', legalEntityCodeData: '' }]
  })

  // ===== Refs =====
  const hasAutoSelectedEntities = useRef(false)

  // ===== Currency State =====
  const [, setCurrency] = useState('AUD')

  // ===== Form Field Error States =====
  const [illegalCharInPurposeDescError, setIllegalCharInPurposeDescError] = useState(false)
  const [illegalCharInOptionalCommentsError, setIllegalCharInOptionalCommentsError] =
    useState(false)
  const [illegalCharInContractNumberError, setIllegalCharContractNumberError] = useState(false)
  const [illegalCharInContractNameError, setIllegalCharContractNameError] = useState(false)
  const [illegalCharInReference, setIllegalCharInReference] = useState(false)

  // ===== Date States =====
  const [startDateValue, setstartDateValue] = useState<Dayjs | null>(null)
  const [endDateValue, setendDateValue] = useState<Dayjs | null>(null)
  const [contractExpiryValue, setContractExpiryValue] = useState<Dayjs | null>(null)

  // ===== Terms & Conditions States =====
  const [isTermsSelected, setIsTermsSelected] = useState(false)
  const [isTermsClicked, setIsTermsClicked] = useState(false)

  const { isLoading: isTermsPdfLoading } = useSelector((state: RootState) => state.pdfReducer)
  const {
    setReqInfo,
    purpose,
    applicants,
    beneficiaries,
    issuers,
    reference,
    expiryDate,
    isOpenEnded,
    type,
    id,
    amount: contextAmount,
    terms: contextTerms
  } = useContext(GlobalInfoContext)
  const initiatedBy = location.pathname.includes('applicant') ? 'applicant' : 'beneficiary'

  const [amountSet, setAmount] = useState(defineAmountState(contextAmount))
  const [terms, setTerms] = useState<Terms>(contextTerms)

  useEffect(() => {
    setTerms(contextTerms)
  }, [contextTerms?.termsDisplay])

  const { onSocketSend } = useSocketConnection()
  const { businessUnitActionContexts: buacs } = useSelector(
    (state: RootState) => state.createGuaranteePayload
  )
  const digitalBuacs = buacs.filter((b) => b.contractType === ContractType.TriContract)
  const { issuers: issuersInReal } = useSelector((root: RootState) => root.termsReducer)

  useEffect(() => {
    if (hasAutoSelectedEntities.current) return

    if (
      initiatedBy === 'applicant' &&
      Array.isArray(digitalBuacs) &&
      digitalBuacs.length === 1 &&
      digitalBuacs[0]?.businessUnitActionContext?.[0]?.legalEntity
    ) {
      const applicantEntity = digitalBuacs[0].businessUnitActionContext[0].legalEntity
      handleEntityValue(applicantEntity, 'applicants')
    }

    if (
      initiatedBy === 'applicant' &&
      Array.isArray(issuersInReal) &&
      issuersInReal?.length === 1 &&
      issuersInReal?.[0]?.issuerLegalEntity
    ) {
      const issuerEntity = issuersInReal?.[0].issuerLegalEntity
      handleEntityValue(issuerEntity, 'issuers')
    }

    hasAutoSelectedEntities.current = true // ✅ run only once
  }, [digitalBuacs, issuersInReal, initiatedBy])

  const pointOfContactSchema = Yup.object().shape({
    position: Yup.string().notRequired(),
    person: Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      phone: Yup.string().required('Phone is required'),
      email: Yup.string().email('Invalid email').required('Email is required')
    })
  })

  const GuaranteeCreateSchema = Yup.object()
    .shape({
      applicants: Yup.string().required('At least one Applicant is required'),

      beneficiaries: Yup.array()
        .of(
          Yup.object().shape({
            commonName: Yup.string().required('Beneficiary ABN/ACN is required'),
            legalEntityIdentifier: Yup.string().required(
              'Please enter a valid beneficiary ABN/ACN'
            ),
            onboarded: Yup.boolean().notRequired(),
            designatedPointOfContact: Yup.mixed().when('onboarded', {
              is: false,
              then: pointOfContactSchema.required('Point of contact is required'),
              otherwise: Yup.mixed().nullable(true)
            })
          })
        )
        .min(1, 'At least one Beneficiary is required'),

      issuers: Yup.string()
        .trim()
        .when([], {
          is: () => location.pathname.includes('beneficiary'),
          then: Yup.string().notRequired(),
          otherwise: Yup.string().required('Issuer Name is required')
        }),

      purposeDescription: Yup.string()
        .trim()
        .min(3)
        .required('Purpose of the guarantee is required'),

      amount: Yup.string().min(1).required('Amount cannot be empty'),

      number: Yup.string().notRequired(),
      name: Yup.string().notRequired(),

      startDate: Yup.date().notRequired(),
      endDate: Yup.date().notRequired(),

      terms: Yup.string().required('Please select terms'),

      isOpenEnded: Yup.boolean().notRequired(),

      expiryDate: Yup.date().when('isOpenEnded', {
        is: false,
        then: Yup.date()
          .required('Expiry Date is required')
          .test('min-expiry-days', getExpiryDateErrorMessage(), function (value) {
            if (!value) return true
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const minDate = new Date(today)
            minDate.setDate(minDate.getDate() + getMinExpiryDays())
            return value >= minDate
          }),
        otherwise: Yup.date().notRequired()
      }),

      reference: Yup.string().notRequired()
    })
    .test('bene-app-test', 'Applicant and Beneficiary cannot be the same', function (values) {
      const applicant = values?.applicants?.toLowerCase()?.trim?.()
      const beneficiary = values?.beneficiaries?.[0]?.commonName?.toLowerCase()?.trim?.()

      if (applicant && beneficiary && applicant === beneficiary) {
        return this.createError({
          path: 'beneficiaries',
          message: 'Applicant and Beneficiary cannot be the same'
        })
      }

      return true
    })
    .test('bene-issuer-test', 'Beneficiary and Issuer cannot be the same', function (values) {
      const issuer = values?.issuers?.toLowerCase()?.trim?.()
      const beneficiary = values?.beneficiaries?.[0]?.commonName?.toLowerCase()?.trim?.()

      if (issuer && beneficiary && issuer === beneficiary) {
        return this.createError({
          path: 'issuers',
          message: 'Beneficiary and Issuer cannot be the same'
        })
      }

      return true
    })

  const formik = useFormik<InitialValues>({
    initialValues: {
      applicants: applicants?.[0]?.commonName || '',
      beneficiaries: [
        {
          legalEntityIdentifier: beneficiaries?.[0]?.legalEntityIdentifier || '',
          commonName: beneficiaries[0].commonName || '',
          onboarded: beneficiaries[0]?.onboarded || false,
          designatedPointOfContact: beneficiaries?.[0]?.designatedPointOfContact
            ? {
                position: beneficiaries?.[0]?.designatedPointOfContact?.position || '',
                person: {
                  firstName: beneficiaries?.[0]?.designatedPointOfContact?.person?.firstName || '',
                  lastName: beneficiaries?.[0]?.designatedPointOfContact?.person?.lastName || '',
                  phone: beneficiaries?.[0]?.designatedPointOfContact?.person?.phone || '',
                  email: beneficiaries?.[0]?.designatedPointOfContact?.person?.email || ''
                }
              }
            : undefined,
          requestedTrustee: beneficiaries?.[0]?.requestedTrustee,
          legalEntityType: beneficiaries?.[0]?.legalEntityType || '',
          handlingInstructions: beneficiaries?.[0]?.handlingInstructions
        }
      ],
      issuers: issuers?.[0]?.commonName || '',
      terms: terms.termsDisplay || '',
      purposeDescription: !id ? '' : (purpose?.purposeDescription || ''),
      number: !id ? '' : (purpose?.contract?.number || ''),
      name: !id ? '' : (purpose?.contract?.name || ''),
      expiryDate: expiryDate || '',
      startDate: !id ? '' : (purpose?.contract?.startDate || ''),
      endDate: !id ? '' : (purpose?.contract?.endDate || ''),
      amount: defineAmountState(contextAmount),
      isOpenEnded: isOpenEnded || false,
      reference: reference || ''
    },
    validationSchema: GuaranteeCreateSchema,
    onSubmit: (values) => {
      setReqInfo({
        reference: values?.reference,
        terms,
        amount: convertAmountStringToObject(amountSet),
        expiryDate: values?.expiryDate,
        isOpenEnded: values?.isOpenEnded,
        purpose: {
          ...purpose,
          purposeDescription: values?.purposeDescription,
          contract: {
            ...purpose?.contract,
            name: values?.name,
            number: values?.number
          }
        }
      })
      navigate(PATH_DASHBOARD.general.guaranteeCreate.review)
    }
  })

  const { errors, touched, getFieldProps } = formik
  const previousValuesRef = useRef<string | null>(null)

  // Persist form data to context/localStorage whenever values change
  useEffect(() => {
    // Don't save on initial load, only on changes after user interaction
    if (
      (Object.keys(formik.touched).length > 0 || formik.dirty) &&
      previousValuesRef.current !== JSON.stringify(formik.values)
    ) {
      previousValuesRef.current = JSON.stringify(formik.values)

      setReqInfo({
        applicants:
          applicants && applicants.length > 0
            ? applicants
            : [{ commonName: formik.values.applicants }],
        beneficiaries:
          beneficiaries && beneficiaries.length > 0 ? beneficiaries : formik.values.beneficiaries,
        issuers: issuers && issuers.length > 0 ? issuers : [{ commonName: formik.values.issuers }],
        amount: convertAmountStringToObject(amountSet),
        reference: formik.values.reference,
        expiryDate: formik.values.expiryDate || '',
        isOpenEnded: formik.values.isOpenEnded,
        purpose: {
          ...purpose,
          purposeDescription: formik.values.purposeDescription,
          contract: {
            ...purpose?.contract,
            name: formik.values.name,
            number: formik.values.number,
            startDate: formik.values.startDate || '',
            endDate: formik.values.endDate || ''
          }
        },
        terms
      })
      // Set isTermsSelected when terms are present
      if (terms?.termsDisplay) {
        setIsTermsSelected(true)
      }
    }
  }, [setReqInfo, terms, amountSet, applicants, beneficiaries, issuers, purpose])

  const handleAmount = (value: string) => {
    formik.setFieldValue('amount', value, true)
    setAmount(value)
  }

  // ===== Error Handler Map =====
  const illegalCharErrorHandlers: Record<string, (value: boolean) => void> = {
    purposeDescription: setIllegalCharInPurposeDescError,
    name: setIllegalCharContractNameError,
    number: setIllegalCharContractNumberError,
    reference: setIllegalCharInReference
  }

  const sanitizeComment = (input: string, type: string) => {
    const { sanitized, mapped, ignored } = sanitizeInput(input)
    const hasIssue = mapped || ignored

    // Update error state for the specific field type
    illegalCharErrorHandlers[type]?.(hasIssue)

    return sanitized
  }
  const childRef = useRef()
  const childRefForTerms = useRef()
  const issuerRef = useRef()
  const handleDate = (value: Dayjs, type: string) => {
    const formattedValue = value?.format('YYYY/MM/DD')

    // Update formik field value
    formik.setFieldValue(type, formattedValue, true)

    // Update local date state based on type
    switch (type) {
      case 'expiryDate':
        setContractExpiryValue(value)
        break
      case 'startDate':
        setstartDateValue(value)
        break
      case 'endDate':
        setendDateValue(value)
        break
    }

    // Update context info
    if (type === 'expiryDate') {
      setReqInfo({
        expiryDate: formattedValue,
        purpose: { ...purpose, contract: { ...purpose?.contract } }
      })
    } else {
      setReqInfo({
        purpose: {
          ...purpose,
          contract: { ...purpose?.contract, [`${type}`]: formattedValue }
        }
      })
    }
  }
  const handleOpenEndedDate = (value) => {
    setReqInfo({
      isOpenEnded: !isOpenEnded,
      expiryDate: null,
      purpose: {
        ...purpose,
        contract: {
          ...purpose?.contract
        }
      }
    })
    value && formik.setFieldValue('expiryDate', '')
    setContractExpiryValue(null)
    formik.setFieldValue('isOpenEnded', value)
  }

  // ===== Entity Value Handler =====
  const resetTermsOnIssuerChange = () => {
    try {
      ;(childRefForTerms?.current as any)?.resetTerms?.()
    } catch (e) {
      // silently ignore if ref not available
    }
    formik.setFieldValue('terms', '', true)
    setTerms({ termsIdentifier: '', termsDisplay: '', deedDisplay: '', deedIdentifier: '' })
    setIsTermsSelected(false)
  }

  const handleEntityValue = (value: LegalEntityParty, type: string) => {
    if (typeof value === 'object') {
      switch (type) {
        case 'applicants':
          formik.setFieldValue('applicants', value?.commonName, true)
          setApplicant(value)
          setReqInfo({ applicants: [value] })
          break

        case 'beneficiaries':
          const beneficiaryEntry = {
            legalEntityIdentifier: value?.legalEntityIdentifier || '',
            // prefer commonName, otherwise fall back to businessIDcomplete, ABN/ACN or identifier
            commonName:
              value?.commonName ||
              (value as any)?.businessIDcomplete ||
              value?.legalEntityCodes?.[0]?.legalEntityCodeData ||
              value?.legalEntityIdentifier ||
              '',
            onboarded: value?.onboarded ?? false,
            // Preserve existing designatedPointOfContact if present, otherwise use value's or undefined
            designatedPointOfContact: value?.designatedPointOfContact || beneficiaries?.[0]?.designatedPointOfContact,
            legalEntityCodes: value?.legalEntityCodes || [],
            requestedTrustee: value?.requestedTrustee,
            legalEntityType: value?.legalEntityType || '',
            handlingInstructions: value?.handlingInstructions
          } as any
          formik.setFieldValue('beneficiaries', [beneficiaryEntry], true)
          setBene(value)
          setReqInfo({ beneficiaries: [beneficiaryEntry] })
          break

        case 'issuers':
          formik.setFieldValue('issuers', value?.commonName || '', true)
          setIssuer(value)
          resetTermsOnIssuerChange()
          setReqInfo({
            issuers: [value],
            terms: {
              termsIdentifier: '',
              termsDisplay: '',
              deedDisplay: '',
              deedIdentifier: ''
            }
          })
          break
      }
    }
  }

  const handlePointOfContact = (poc: PointOfContact, type: string) => {
    if (type === 'beneficiaries') {
      const current = formik.values.beneficiaries?.[0] ?? { legalEntityIdentifier: '' }
      const updated = {
        ...current,
        designatedPointOfContact: poc
      }

      formik.setFieldValue('beneficiaries', [updated], true)
      setReqInfo({ beneficiaries: [updated] })
    }
  }

  const handleTerms = (value: Terms) => {
    formik.setFieldValue('terms', value.termsDisplay, true)
    setTerms(value)
    setReqInfo({
      terms: value
    })
  }
  const handleClear = (type: string) => {
    if (type === 'applicants') {
      formik.setFieldValue('applicants', '', true)
      setReqInfo({
        applicants: []
      })
    } else if (type === 'beneficiaries') {
      formik.setFieldValue('beneficiaries', '', true)
      setReqInfo({
        beneficiaries: []
      })
    } else if (type === 'issuers') {
      formik.setFieldValue('issuers', '', true)

      setReqInfo({
        issuers: []
      })
    }
  }

  const exitCreationProcess = () => {
    navigate(PATH_DASHBOARD.general.app)
    setReqInfo(initialState)
  }
  // Usage
  async function executePreviewTerms(documentIdentifier: string, roleIdentifier: string) {
    dispatch(startLoadingPDF())
    await sendPDFCall(documentIdentifier, roleIdentifier, onSocketSend)
  }

  const handleReviewAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      formik.handleSubmit()
    } else {
      // Handle nested beneficiaries touched state
      const touchedFields: any = {}

      Object.keys(errors).forEach((key) => {
        if (key === 'beneficiaries' && Array.isArray(errors.beneficiaries)) {
          touchedFields.beneficiaries = errors.beneficiaries.map((err) => {
            if (typeof err === 'object' && err !== null) {
              return Object.fromEntries(Object.keys(err).map((k) => [k, true]))
            }
            return true
          })
        } else {
          touchedFields[key] = true
        }
      })

      formik.setTouched(touchedFields, true)

      const firstErrorField = Object.keys(errors)[0]
      const el = document.querySelector(`[name="${firstErrorField}"]`)
      if (el && 'scrollIntoView' in el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }
  useEffect(() => {
    isTermsClicked && !isTermsPdfLoading && base64ToPdf('terms.pdf', 'pdf')
  }, [isTermsPdfLoading, isTermsClicked])

  const beneficiaryErrors = errors?.beneficiaries?.[0] || {}
  const beneficiaryTouched = touched?.beneficiaries?.[0] || {}
  return (
    <div>
      <form onSubmit={handleReviewAndSubmit}>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Parties</Heading>
          </Grid>
          {initiatedBy === 'applicant' ? (
            <div>
              <ElementBreak>
                <EntityDetailsRow
                  label="Applicant Name"
                  type="applicants"
                  touched={touched}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'applicants')}
                  initialOption={undefined} // optional now, we'll fetch from initialValues
                  singleEntry={true}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={digitalBuacs}
                  ref={childRef}
                  resetIssuer={issuerRef}
                />
              </ElementBreak>
              <ElementBreak>
                <EntityDetailsRow
                  label="Beneficiary Name"
                  type="beneficiaries"
                  touched={beneficiaryTouched}
                  errors={beneficiaryErrors}
                  key={`${type}` ? `bene-${type}` : 'bene-section '}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'beneficiaries')}
                  initialOption={beneficiaries[0]}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={[]}
                  resetTermsRef={childRefForTerms}
                  ref={childRef}
                  resetIssuer={issuerRef}
                  setPOC={(value) => value && handlePointOfContact(value, 'beneficiaries')}
                  isOnbordedRequired={beneficiaries?.[0]?.onboarded}
                />
              </ElementBreak>
              <ElementBreak>
                {typeof formik.errors.beneficiaries === 'string' && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {formik.errors.beneficiaries}
                  </Typography>
                )}
              </ElementBreak>
            </div>
          ) : null}
          {initiatedBy === 'applicant' && (
            <>
              <ElementBreak>
                <EntityDetailsRow
                  label="Issuer Name"
                  type="issuers"
                  touched={touched}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  key={`${type}` ? `issuer-${type}` : 'issuer-section '}
                  setValue={(value) => handleEntityValue(value, 'issuers')}
                  initialOption={issuers[0]}
                  handleClear={handleClear}
                  singleEntry={issuersInReal?.[0]?.issuerLegalEntity.length === 1}
                  initialValues={issuersInReal}
                  initiatedBy={initiatedBy}
                  resetTermsRef={childRefForTerms}
                  issuerRef={issuerRef}
                  ref={childRef}
                  clearable={false}
                  disabled
                />
              </ElementBreak>
            </>
          )}
        </SectionBreak>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Guarantee Purpose</Heading>
          </Grid>
          <ElementBreak>
            <ContractDetailsFields
              numberValue={formik.values.number || ''}
              nameValue={formik.values.name || ''}
              onNumberChange={(value) => formik.setFieldValue('number', value)}
              onNameChange={(value) => formik.setFieldValue('name', value)}
              onNumberError={setIllegalCharContractNumberError}
              onNameError={setIllegalCharContractNameError}
              numberError={Boolean(
                (touched.number && errors.number) || illegalCharInContractNumberError
              )}
              nameError={Boolean((touched?.name && errors?.name) || illegalCharInContractNameError)}
              numberHelperText={touched.number && errors.number ? errors.number : ''}
              nameHelperText={touched?.name && errors?.name ? errors?.name : ''}
            />
          </ElementBreak>
          <ElementBreak>
            <Grid container spacing={2} sx={{ marginBottom: '10px' }}>
              <TimeRow
                startDateValue={startDateValue}
                endDateValue={endDateValue}
                minDate={startDateValue}
                maxDate={endDateValue}
                setDateValue={handleDate}
                openEnded={formik.values.isOpenEnded}
                setOpenEnded={handleOpenEndedDate}
                errors={errors}
                touched={touched}
                disablePast={false}
              />
            </Grid>
          </ElementBreak>
          <ElementBreak>
            <Grid item xs={12}>
              <Item>
                <TextField
                  id="purpose-of-the-guarantee"
                  multiline
                  rows={10}
                  placeholder="The detailed description of the contract (Mandatory)"
                  label="Purpose of the Guarantee (Mandatory)"
                  value={formik.values.purposeDescription || purpose?.purposeDescription}
                  onChange={(e) => {
                    const cleaned = sanitizeComment(e.target.value, 'purposeDescription')
                    if (formik.values.purposeDescription !== cleaned) {
                      formik.setFieldValue('purposeDescription', cleaned)
                    }
                  }}
                  size="medium"
                  type="text"
                  autoComplete="off"
                  style={{ width: '100%' }}
                  InputLabelProps={{ shrink: true }}
                  error={
                    Boolean(touched.purposeDescription && errors.purposeDescription) ||
                    illegalCharInPurposeDescError
                  }
                  helperText={
                    touched.purposeDescription && errors.purposeDescription
                      ? errors.purposeDescription
                      : illegalCharInPurposeDescError
                      ? illegalCharacterMessage
                      : ''
                  }
                  inputProps={{ maxLength: 1000 }}
                />
              </Item>
            </Grid>
          </ElementBreak>
        </SectionBreak>
        <SectionBreak>
          <ElementBreak>
            <Heading>Guarantee Details</Heading>
          </ElementBreak>
          <ElementBreak>
            <ExpiryDate
              expiryDateValue={contractExpiryValue}
              setExpiryDate={handleDate}
              openEnded={formik.values.isOpenEnded}
              handleOpenEndedDate={handleOpenEndedDate}
              errors={errors}
              touched={touched}
              data-testid="expiry"
            />
          </ElementBreak>
          <ElementBreak>
            <AmountAndCurrency
              setCurrency={setCurrency}
              amount={amountSet}
              setAmount={handleAmount}
              touched={touched}
              errors={errors}
            />
          </ElementBreak>

          <ElementBreak>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <TermsAndConditionsSelection
                setIsTermsSelected={setIsTermsSelected}
                handleEntityValue={handleTerms}
                ref={childRefForTerms}
                errors={errors}
                touched={touched}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1} lg={1} />
            <Grid item xs={12} sm={6} md={2} lg={2} sx={{ marginTop: { xs: '10px', sm: '10px' } }}>
              {isTermsSelected && (
                <Link
                  to="#"
                  style={{
                    color: PRIMARY.main
                  }}
                  onClick={() => {
                    setIsTermsClicked(true)
                    executePreviewTerms(
                      terms.termsIdentifier ?? '',
                      digitalBuacs[0]?.businessUnitActionContext[0]?.workflowRole?.roleIdentifier ||
                        null
                    )
                  }}
                >
                  Preview Terms
                </Link>
              )}
            </Grid>
          </ElementBreak>
        </SectionBreak>
        <SectionBreak>
          <InternalReferenceField
            value={formik?.values?.reference || ''}
            onChange={(value) => formik.setFieldValue('reference', value)}
            error={illegalCharInReference}
            helperText={illegalCharInReference ? illegalCharacterMessage : ''}
            onSanitizationError={setIllegalCharInReference}
          />
        </SectionBreak>

        <Box sx={{ marginTop: '2rem' }}>
          <Grid container spacing={{ xs: 1, sm: 4 }}>
            <Grid item xs={12} sm="auto">
              <Button sx={{ ...commonNoActionButtonStyle, width: { xs: '100%', sm: 'auto' } }} onClick={exitCreationProcess}>
                Exit
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                type="submit"
                data-testid="review-and-submit-btn"
                id="review-and-submit-btn"
                sx={{ ...commonProceedButtonStyle, width: { xs: '100%', sm: 'auto' } }}
              >
                Review and Submit
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    </div>
  )
}

export default PaymentPerformanceGuarantee
